import type { Contract, Milestone } from '../types';
import { contractTitle } from './contractTitle';
import {
  pendingTimelineReplacement,
  timelineFundingNeedOre
} from './timelineFunding';

/** The effective payment plan can span several agreements. Display unchanged
 * work under its original agreement and retain the signed changes as history. */
export function agreementTimelines(
  current: Contract,
  contracts: Contract[],
  stages: Milestone[]
) {
  const chain: Contract[] = [current];
  const visited = new Set([current.id]);
  let head = current;
  while (head.stageVariation) {
    const parentId = head.stageVariation.originalContractId;
    const parent = contracts.find(
      (item) => item.id === parentId && item.projectId === current.projectId
    );
    if (!parent || visited.has(parent.id)) break;
    visited.add(parent.id);
    chain.unshift(parent);
    head = parent;
  }
  const pending = pendingTimelineReplacement(current, contracts) ?? contracts.find(
    (candidate) => candidate.projectId === current.projectId &&
      candidate.stageVariation?.originalContractId === current.id &&
      candidate.versionStatus === 'proposed' && candidate.draftStatus !== 'draft_rejected' &&
      (candidate.companySigned || candidate.personSigned ||
        ['sent', 'pending_signature', 'signed'].includes(candidate.signingStatus ?? ''))
  );
  if (pending && !pending.stageVariation) return null;
  if (pending) chain.push(pending);
  if (chain.length === 1) return null;
  const plan = (contractId: string) =>
    stages.filter((stage) => stage.contractId === contractId);
  const originalStage = (source: Milestone) => {
    let original = source;
    const seen = new Set<string>();
    while (original.replacesMilestoneId && !seen.has(original.id)) {
      seen.add(original.id);
      const contractId = original.contractId;
      const owner = chain.find((item) => item.id === contractId);
      if (
        !owner?.stageVariation ||
        owner.stageVariation.selectedMilestoneIds.includes(
          original.replacesMilestoneId
        )
      )
        break;
      const previousId = original.replacesMilestoneId;
      const parentId = owner.stageVariation.originalContractId;
      const previous = stages.find(
        (item) => item.id === previousId && item.contractId === parentId
      );
      if (!previous) break;
      original = previous;
    }
    return {
      contractId: original.contractId,
      contractNumber:
        chain.find((item) => item.id === original.contractId)?.contractNumber ??
        0,
      milestoneId: original.id,
      milestoneNumber: original.milestoneNumber
    };
  };
  return chain.map((agreement, index) => {
    const ownStages = plan(agreement.id).filter(
      (stage) =>
        !agreement.stageVariation ||
        !stage.replacesMilestoneId ||
        agreement.stageVariation.selectedMilestoneIds.includes(
          stage.replacesMilestoneId
        )
    );
    const milestones: Milestone[] = ownStages.map((original) => {
      let stage = original;
      for (const next of chain.slice(index + 1)) {
        const stageId = stage.id;
        const successor = plan(next.id).find(
          (item) => item.replacesMilestoneId === stageId
        );
        if (next.stageVariation?.selectedMilestoneIds.includes(stage.id)) {
          return {
            ...original,
            continuedBy: undefined,
            stageReplacement: {
              kind: successor ? ('replaced' as const) : ('removed' as const),
              contractId: next.id,
              contractNumber: next.contractNumber,
              description: contractTitle(next),
              milestoneId: successor?.id ?? null,
              originalStage: originalStage(original)
            }
          };
        }
        if (next.id === pending?.id || !successor) break;
        stage = successor;
      }
      return { ...stage, stageReplacement: undefined, continuedBy: undefined };
    });
    for (const source of plan(
      agreement.stageVariation?.originalContractId ?? ''
    )) {
      if (
        !agreement.stageVariation?.selectedMilestoneIds.includes(source.id) ||
        plan(agreement.id).some(
          (item) => item.replacesMilestoneId === source.id
        )
      )
        continue;
      milestones.push({
        ...source,
        continuedBy: undefined,
        stageReplacement: {
          kind: 'removed',
          contractId: agreement.id,
          contractNumber: agreement.contractNumber,
          description: contractTitle(agreement),
          milestoneId: null,
          originalStage: originalStage(source)
        }
      });
    }
    const awaitingActivation = agreement.id === pending?.id || (!!agreement.stageVariation && (
      !agreement.companySigned || !agreement.personSigned ||
      ['proposed', 'settlement_pending', 'activation_failed'].includes(agreement.versionStatus ?? 'current')
    ));
    const fundingNeed = awaitingActivation
      ? timelineFundingNeedOre(agreement)
      : 0;
    const gross = agreement.stageVariation
      ? agreement.stageVariation.grossDeltaOre / 100
      : Number(agreement.contractAmount);
    const net = agreement.stageVariation
      ? agreement.stageVariation.afterRotDeltaOre / 100
      : Number(agreement.afterRotAmount);
    return {
      contract: {
        ...agreement,
        // Original contracts remain operational presentation groups; actual
        // milestone IDs still address the current effective contract.
        versionStatus: awaitingActivation
          ? ('settlement_pending' as const)
          : ('current' as const),
        contractAmount: String(gross),
        afterRotAmount: String(net),
        paidAmount: Math.max(0, net - fundingNeed / 100),
        remainingAmount: fundingNeed / 100
      },
      milestones: awaitingActivation
        ? milestones.map((stage) => {
            if (stage.stageReplacement) return stage;
            const source = stage.replacesMilestoneId
              ? stages.find(
                  (item) =>
                    item.id === stage.replacesMilestoneId &&
                    item.contractId ===
                      agreement.stageVariation?.originalContractId
                )
              : undefined;
            const started =
              source &&
              (source.workStartedAt ||
                (source.companyStartDecision !== 'not_started' &&
                  source.deliveryStatus &&
                  source.deliveryStatus !== 'not_started'));
            return {
              ...stage,
              deliveryStatus: started
                ? ('in_progress' as const)
                : ('not_started' as const),
              workStartedAt: started ? source.workStartedAt : null,
              workStartedByRole: started ? source.workStartedByRole : null,
              startPredecessorIds: started
                ? (source.startPredecessorIds ?? []).flatMap((id) => {
                    const predecessor = milestones.find(
                      (item) => item.replacesMilestoneId === id
                    );
                    return predecessor ? [predecessor.id] : [];
                  })
                : []
            };
          })
        : milestones,
      awaitingActivation,
      funded: fundingNeed === 0
    };
  });
}
